import { useCallback, useEffect, useMemo, useState } from "react";
import { Search, X } from "lucide-react";

import {
  PoliciesService,
  type CreatePolicyRequest,
  type PolicyRuleDefinition,
  type PolicyTemplateResponse,
  type PolicyVersionResponse,
} from "@/api";
import { useApi, useMutation } from "@/hooks/use-api";
import { useAuth } from "@/hooks/use-auth";
import { useTranslation } from "@/i18n";
import {
  parseApiError,
  parseValidationDetail,
} from "@/lib/api-error";
import { usePlaygroundCatalog } from "@/playground/use-catalog";
import { Alert } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogBody,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { FormField } from "@/components/ui/form-field";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { JsonEditor } from "@/components/shared/json-editor";
import { cn } from "@/lib/utils";

interface PolicyCreateDialogProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

const SLUG_PATTERN = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;

type DialogMode = "model-access" | "advanced";

const MODEL_ACCESS_DEFAULTS = {
  name: "Model access control",
  slug: "model-access-control",
  priority: "90",
  denyReasonCode: "MODEL_NOT_ALLOWED",
} as const;

type ScopeChoice = "workspace" | "tenant";

/** Build the allowlist DSL rule from the wizard selections. Stays
 *  schema-compatible with ``PolicyRuleDefinition`` so it flows through
 *  the existing /v1/admin/governance/policies endpoint untouched. */
function buildModelAccessRule(
  allowed: string[],
  priority: number,
  denyMessage: string
): PolicyRuleDefinition {
  return {
    name: MODEL_ACCESS_DEFAULTS.name,
    description: "Allowlist of models permitted for this scope.",
    when: {
      operator: "and",
      conditions: [
        {
          field: "request.model",
          operator: "not_in",
          value: allowed,
        },
      ],
    },
    effect: {
      type: "deny",
      deny_reason_code: MODEL_ACCESS_DEFAULTS.denyReasonCode,
      deny_user_message: denyMessage,
    },
    priority,
  };
}

/** Empty DSL skeleton — matches the backend ``PolicyRuleDefinition``
 *  Pydantic schema exactly. Operator names use the backend's
 *  ``ConditionOperator`` StrEnum spelling (``equals``, not ``eq``). */
const EMPTY_DSL: PolicyRuleDefinition = {
  name: "Nama kebijakan",
  description: "",
  when: {
    operator: "and",
    conditions: [
      {
        field: "user.team",
        operator: "equals",
        value: "<team>",
      },
    ],
  },
  effect: {
    type: "deny",
    deny_reason_code: "<CODE>",
    deny_user_message: "Pesan singkat untuk user (Bahasa Indonesia).",
  },
  priority: 50,
};

function slugify(name: string): string {
  return name
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)+/g, "");
}

export function PolicyCreateDialog({
  open,
  onClose,
  onSuccess,
}: PolicyCreateDialogProps) {
  const { user } = useAuth();
  const { t } = useTranslation();
  const [mode, setMode] = useState<DialogMode>("model-access");
  const [name, setName] = useState("");
  const [slug, setSlug] = useState("");
  const [slugDirty, setSlugDirty] = useState(false);
  const [description, setDescription] = useState("");
  const [priority, setPriority] = useState("50");
  const [selectedTemplateSlug, setSelectedTemplateSlug] = useState("");
  const [ruleJson, setRuleJson] = useState(() =>
    JSON.stringify(EMPTY_DSL, null, 2)
  );
  const [validRule, setValidRule] = useState<PolicyRuleDefinition | null>(
    null
  );
  const [validationDetails, setValidationDetails] = useState<string[]>([]);

  // Sesi MODEL-ACCESS-CONTROL — wizard state. Allowed-models multi-select
  // sourced from the playground catalog so it stays in sync with the
  // dispatchable model surface; scope toggle defaults to "workspace" per
  // the product decision (tenant fall-through still supported).
  const [allowedModels, setAllowedModels] = useState<string[]>([]);
  const [modelQuery, setModelQuery] = useState("");
  const [scope, setScope] = useState<ScopeChoice>("workspace");

  const templatesFetcher = useCallback(
    () => PoliciesService.listTemplates(),
    []
  );
  const templates = useApi<PolicyTemplateResponse[]>(templatesFetcher, {
    enabled: open && mode === "advanced",
  });

  const catalog = usePlaygroundCatalog();
  // Flat dispatchable model list. ``has_active`` providers only — same
  // semantics as the playground selector, so the admin can't accidentally
  // allowlist a model the tenant has no credential to call.
  const catalogModels = useMemo<string[]>(() => {
    if (!catalog.catalog) return [];
    return Array.from(
      new Set(
        catalog.catalog.providers
          .filter((p) => p.has_active)
          .flatMap((p) => p.models)
      )
    );
  }, [catalog.catalog]);

  const { mutate, isLoading, error, reset } = useMutation<
    CreatePolicyRequest,
    PolicyVersionResponse
  >((body) => PoliciesService.create(body), {
    onSuccess: () => {
      setName("");
      setSlug("");
      setSlugDirty(false);
      setDescription("");
      setPriority("50");
      setSelectedTemplateSlug("");
      setRuleJson(JSON.stringify(EMPTY_DSL, null, 2));
      setAllowedModels([]);
      setModelQuery("");
      setScope("workspace");
      setValidationDetails([]);
      onSuccess();
    },
    onError: (err) => {
      setValidationDetails(parseValidationDetail(err));
    },
  });

  // Pre-populate sensible defaults when the wizard mode is first opened
  // so a tenant admin can save with one click after picking the models.
  // Only fires when the form is empty — never clobbers a user edit.
  useEffect(() => {
    if (mode !== "model-access") return;
    if (!name) setName(MODEL_ACCESS_DEFAULTS.name);
    if (!slug) {
      setSlug(MODEL_ACCESS_DEFAULTS.slug);
      setSlugDirty(true);
    }
    if (priority === "50") setPriority(MODEL_ACCESS_DEFAULTS.priority);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode]);

  const handleNameChange = (next: string) => {
    setName(next);
    if (!slugDirty) setSlug(slugify(next));
  };

  const handleSlugChange = (next: string) => {
    setSlugDirty(true);
    setSlug(next);
  };

  const handleTemplateChange = (templateSlug: string) => {
    setSelectedTemplateSlug(templateSlug);
    if (!templateSlug) return;
    const tpl = templates.data?.find((t) => t.slug === templateSlug);
    if (!tpl) return;
    // Populate fields from template so the user lands in a working
    // state. Slug picks up the template slug as a sensible default
    // (user can override).
    setName(tpl.template_name);
    setSlug(tpl.slug);
    setSlugDirty(true); // freeze auto-slug from name
    setDescription(tpl.rule_definition.description ?? "");
    setPriority(String(tpl.rule_definition.priority ?? 50));
    setRuleJson(JSON.stringify(tpl.rule_definition, null, 2));
  };

  const handleClose = () => {
    reset();
    setValidationDetails([]);
    onClose();
  };

  const slugIsValid = slug.length === 0 || SLUG_PATTERN.test(slug);
  const wizardAllowedValid = allowedModels.length > 0;
  const hasUsableValidRule =
    mode === "model-access" ? wizardAllowedValid : validRule !== null;
  // Wizard mode without a workspace context AND scope=workspace would
  // silently fall back to tenant scope — block that ambiguity instead.
  const wizardScopeValid =
    mode !== "model-access" ||
    scope === "tenant" ||
    Boolean(user?.workspace_id);
  const canSubmit =
    name.trim().length > 0 &&
    slug.length > 0 &&
    slugIsValid &&
    hasUsableValidRule &&
    wizardScopeValid &&
    Boolean(user?.tenant_id);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSubmit || !user?.tenant_id) return;
    setValidationDetails([]);

    let ruleDefinition: PolicyRuleDefinition | null;
    if (mode === "model-access") {
      if (!wizardAllowedValid) return;
      ruleDefinition = buildModelAccessRule(
        allowedModels,
        Number(priority) || Number(MODEL_ACCESS_DEFAULTS.priority),
        t("policies.modelAccess.denyMessage")
      );
    } else {
      ruleDefinition = validRule;
    }
    if (!ruleDefinition) return;

    const body: CreatePolicyRequest = {
      tenant_id: user.tenant_id,
      name: name.trim(),
      slug,
      priority: Number(priority) || 50,
      rule_definition: ruleDefinition,
    };
    const trimmedDesc = description.trim();
    if (trimmedDesc) body.description = trimmedDesc;
    // Wizard scope choice wins; the legacy "advanced" mode keeps the
    // workspace-from-principal behaviour so existing flows are unchanged.
    if (mode === "model-access") {
      if (scope === "workspace" && user.workspace_id) {
        body.workspace_id = user.workspace_id;
      }
    } else if (user.workspace_id) {
      body.workspace_id = user.workspace_id;
    }
    await mutate(body);
  };

  const toggleModel = (model: string) => {
    setAllowedModels((prev) =>
      prev.includes(model) ? prev.filter((m) => m !== model) : [...prev, model]
    );
  };

  // Catalogs can list dozens of models. Filter by a case-insensitive
  // substring so the operator searches instead of scanning a wall of
  // checkboxes; "select all" then acts on the *visible* matches so a
  // search + select-all picks exactly the family they typed.
  const filteredModels = useMemo<string[]>(() => {
    const q = modelQuery.trim().toLowerCase();
    if (!q) return catalogModels;
    return catalogModels.filter((m) => m.toLowerCase().includes(q));
  }, [catalogModels, modelQuery]);

  const allFilteredSelected =
    filteredModels.length > 0 &&
    filteredModels.every((m) => allowedModels.includes(m));

  const selectAllFiltered = () => {
    setAllowedModels((prev) => Array.from(new Set([...prev, ...filteredModels])));
  };

  const clearAllModels = () => setAllowedModels([]);

  // Reset transient form state when the dialog is opened fresh.
  useEffect(() => {
    if (open) setValidationDetails([]);
  }, [open]);

  return (
    <Dialog open={open} onClose={handleClose} size="max-w-2xl">
      <DialogHeader>
        <DialogTitle>{t("policies.createDialog.title")}</DialogTitle>
        <DialogDescription>
          {t("policies.createDialog.description")}
        </DialogDescription>
      </DialogHeader>

      <form onSubmit={handleSubmit}>
        <DialogBody>
          {/* Sesi MODEL-ACCESS-CONTROL — mode segmented control. The
              wizard generates a normal PolicyRuleDefinition; advanced
              keeps the raw-DSL editor for non-trivial rules. */}
          <div
            role="tablist"
            aria-label={t("policies.createDialog.modeAria")}
            className="inline-flex rounded-md border border-ocean-100 bg-ocean-50/40 p-0.5"
          >
            {(["model-access", "advanced"] as DialogMode[]).map((m) => {
              const active = mode === m;
              return (
                <button
                  key={m}
                  type="button"
                  role="tab"
                  aria-selected={active}
                  onClick={() => setMode(m)}
                  className={cn(
                    "rounded px-3 py-1.5 text-xs font-medium transition-colors",
                    active
                      ? "bg-white text-ocean-900 shadow-sm"
                      : "text-neutral-600 hover:text-ocean-900"
                  )}
                >
                  {m === "model-access"
                    ? t("policies.createDialog.modeModelAccess")
                    : t("policies.createDialog.modeAdvanced")}
                </button>
              );
            })}
          </div>

          {/* Template picker — advanced mode only */}
          {mode === "advanced" && (
          <div className="space-y-1.5">
            <label
              htmlFor="policy-template"
              className="block text-sm font-medium text-ocean-900"
            >
              {t("policies.createDialog.templateLabel")}
            </label>
            <select
              id="policy-template"
              value={selectedTemplateSlug}
              onChange={(e) => handleTemplateChange(e.target.value)}
              disabled={templates.isLoading || isLoading}
              className={cn(
                "h-10 w-full rounded-md border bg-white px-3 text-sm transition-colors focus:outline-none focus:ring-2 focus:ring-ocean-400",
                "border-ocean-100",
                "disabled:cursor-not-allowed disabled:bg-ocean-50/50"
              )}
            >
              <option value="">
                {t("policies.createDialog.templateNone")}
              </option>
              {templates.data?.map((tpl) => (
                <option key={tpl.slug} value={tpl.slug}>
                  {tpl.template_name}
                </option>
              ))}
            </select>
            {selectedTemplateSlug && (
              <p className="text-xs text-neutral-600">
                {templates.data?.find(
                  (t) => t.slug === selectedTemplateSlug
                )?.summary}
              </p>
            )}
            {templates.error && (
              <p className="text-xs text-warning-600">
                {t("policies.createDialog.templateError", {
                  detail: parseApiError(templates.error),
                })}
              </p>
            )}
          </div>
          )}

          <div className="grid grid-cols-1 gap-4 tablet:grid-cols-2">
            <FormField
              label={t("policies.createDialog.nameLabel")}
              value={name}
              onChange={handleNameChange}
              placeholder={t("policies.createDialog.namePlaceholder")}
              maxLength={200}
              required
              autoFocus
            />
            <FormField
              label={t("policies.createDialog.slugLabel")}
              value={slug}
              onChange={handleSlugChange}
              placeholder={t("policies.createDialog.slugPlaceholder")}
              maxLength={100}
              required
              error={
                slug.length > 0 && !slugIsValid
                  ? t("policies.createDialog.slugError")
                  : undefined
              }
              hint={t("policies.createDialog.slugHint")}
            />
          </div>
          <FormField
            label={t("policies.createDialog.descriptionLabel")}
            value={description}
            onChange={setDescription}
            placeholder={t("policies.createDialog.descriptionPlaceholder")}
            maxLength={500}
          />
          <FormField
            label={t("policies.createDialog.priorityLabel")}
            type="number"
            value={priority}
            onChange={setPriority}
            min={0}
            max={100}
            hint={t("policies.createDialog.priorityHint")}
          />

          {mode === "advanced" ? (
            <div className="space-y-1.5">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-ocean-900">
                  {t("policies.createDialog.ruleLabel")}
                </span>
                <span className="text-xs text-neutral-500">
                  {t("policies.createDialog.ruleHint")}
                </span>
              </div>
              <JsonEditor
                value={ruleJson}
                onChange={setRuleJson}
                onValidChange={(parsed) =>
                  setValidRule(
                    parsed && typeof parsed === "object"
                      ? (parsed as PolicyRuleDefinition)
                      : null
                  )
                }
                rows={12}
                placeholder={JSON.stringify(EMPTY_DSL, null, 2)}
              />
            </div>
          ) : (
            <div className="space-y-4 rounded-md border border-ocean-100 bg-ocean-50/30 p-4">
              <div className="space-y-1.5">
                <p className="text-sm font-medium text-ocean-900">
                  {t("policies.modelAccess.intro")}
                </p>
                <p className="text-xs text-neutral-600">
                  {t("policies.modelAccess.introDetail")}
                </p>
              </div>

              <fieldset className="space-y-1.5">
                <legend className="text-sm font-medium text-ocean-900">
                  {t("policies.modelAccess.scopeLabel")}
                </legend>
                <div className="flex flex-col gap-2 tablet:flex-row tablet:gap-6">
                  {(["workspace", "tenant"] as ScopeChoice[]).map((s) => {
                    const labelKey =
                      s === "workspace"
                        ? "policies.modelAccess.scopeWorkspace"
                        : "policies.modelAccess.scopeTenant";
                    const disabled =
                      s === "workspace" && !user?.workspace_id;
                    return (
                      <label
                        key={s}
                        className={cn(
                          "inline-flex items-center gap-2 text-sm",
                          disabled
                            ? "cursor-not-allowed text-neutral-400"
                            : "cursor-pointer text-ocean-900"
                        )}
                      >
                        <input
                          type="radio"
                          name="model-access-scope"
                          value={s}
                          checked={scope === s}
                          disabled={disabled}
                          onChange={() => setScope(s)}
                          className="h-4 w-4 text-ocean-600 focus:ring-ocean-400"
                        />
                        {t(labelKey)}
                      </label>
                    );
                  })}
                </div>
                {!user?.workspace_id && (
                  <p className="text-xs text-neutral-500">
                    {t("policies.modelAccess.scopeNoWorkspaceHint")}
                  </p>
                )}
              </fieldset>

              <fieldset className="space-y-2">
                <legend className="text-sm font-medium text-ocean-900">
                  {t("policies.modelAccess.modelsLabel")}
                </legend>
                {catalog.loading && !catalog.catalog ? (
                  <Skeleton className="h-24 w-full" />
                ) : catalog.error ? (
                  <Alert variant="warning">
                    {t("policies.modelAccess.catalogError", {
                      detail: catalog.error,
                    })}
                  </Alert>
                ) : catalogModels.length === 0 ? (
                  <Alert variant="info">
                    {t("policies.modelAccess.catalogEmpty")}
                  </Alert>
                ) : (
                  <div className="space-y-2">
                    {/* Search + bulk actions. Searching narrows the list;
                        "select all" then acts on the visible matches. */}
                    <div className="flex flex-col gap-2 tablet:flex-row tablet:items-center">
                      <div className="relative flex-1">
                        <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-neutral-400" />
                        <Input
                          type="text"
                          value={modelQuery}
                          onChange={(e) => setModelQuery(e.target.value)}
                          placeholder={t(
                            "policies.modelAccess.searchPlaceholder"
                          )}
                          aria-label={t(
                            "policies.modelAccess.searchPlaceholder"
                          )}
                          className="h-9 pl-8"
                        />
                      </div>
                      <div className="flex shrink-0 items-center gap-2">
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={selectAllFiltered}
                          disabled={
                            filteredModels.length === 0 || allFilteredSelected
                          }
                        >
                          {modelQuery.trim()
                            ? t("policies.modelAccess.selectFiltered", {
                                count: filteredModels.length,
                              })
                            : t("policies.modelAccess.selectAll")}
                        </Button>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={clearAllModels}
                          disabled={allowedModels.length === 0}
                        >
                          {t("policies.modelAccess.clear")}
                        </Button>
                      </div>
                    </div>

                    {/* Selected models as removable chips so the choice
                        stays visible without scrolling the list. */}
                    {allowedModels.length > 0 && (
                      <div className="flex flex-wrap gap-1.5">
                        {allowedModels.map((model) => (
                          <span
                            key={model}
                            className="inline-flex items-center gap-1 rounded-full border border-ocean-200 bg-ocean-50 py-0.5 pl-2 pr-1 text-xs text-ocean-900"
                          >
                            <code className="text-xs">{model}</code>
                            <button
                              type="button"
                              onClick={() => toggleModel(model)}
                              aria-label={t(
                                "policies.modelAccess.removeModel",
                                { model }
                              )}
                              className="rounded-full p-0.5 text-ocean-500 transition-colors hover:bg-ocean-100 hover:text-ocean-800"
                            >
                              <X className="h-3 w-3" />
                            </button>
                          </span>
                        ))}
                      </div>
                    )}

                    {/* Scrollable filtered list — capped height so a long
                        catalog never blows up the dialog. */}
                    {filteredModels.length === 0 ? (
                      <p className="rounded-md border border-ocean-100 bg-white px-3 py-6 text-center text-xs text-neutral-500">
                        {t("policies.modelAccess.searchEmpty", {
                          query: modelQuery.trim(),
                        })}
                      </p>
                    ) : (
                      <div className="max-h-56 space-y-1 overflow-y-auto rounded-md border border-ocean-100 bg-white p-1.5">
                        {filteredModels.map((model) => {
                          const checked = allowedModels.includes(model);
                          return (
                            <label
                              key={model}
                              className={cn(
                                "flex cursor-pointer items-center gap-2 rounded px-2.5 py-1.5 text-sm transition-colors",
                                checked
                                  ? "bg-ocean-50 text-ocean-900"
                                  : "text-neutral-700 hover:bg-ocean-50/50"
                              )}
                            >
                              <input
                                type="checkbox"
                                checked={checked}
                                onChange={() => toggleModel(model)}
                                className="h-4 w-4 text-ocean-600 focus:ring-ocean-400"
                              />
                              <code className="text-xs">{model}</code>
                            </label>
                          );
                        })}
                      </div>
                    )}
                  </div>
                )}
                <p
                  className={cn(
                    "text-xs",
                    wizardAllowedValid
                      ? "text-neutral-500"
                      : "text-danger-600"
                  )}
                  role={wizardAllowedValid ? undefined : "alert"}
                >
                  {wizardAllowedValid
                    ? t("policies.modelAccess.modelsHint", {
                        count: allowedModels.length,
                      })
                    : t("policies.modelAccess.modelsRequiredError")}
                </p>
              </fieldset>
            </div>
          )}

          {error && (
            <Alert variant="danger">
              <div className="space-y-1.5">
                <div className="text-sm font-medium">
                  {parseApiError(error)}
                </div>
                {validationDetails.length > 0 && (
                  <ul className="ml-4 list-disc text-xs">
                    {validationDetails.map((line, idx) => (
                      <li key={idx}>{line}</li>
                    ))}
                  </ul>
                )}
              </div>
            </Alert>
          )}
        </DialogBody>

        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={handleClose}
            disabled={isLoading}
          >
            {t("policies.createDialog.cancel")}
          </Button>
          <Button type="submit" disabled={isLoading || !canSubmit}>
            {isLoading
              ? t("policies.createDialog.submitLoading")
              : t("policies.createDialog.submit")}
          </Button>
        </DialogFooter>
      </form>
    </Dialog>
  );
}
